// pages/VendorShowcasePage.jsx
import React, { useState } from 'react';
import { ArrowLeft, Users, TrendingUp, DollarSign, BarChart4, ShoppingBag, Store, Star, CheckCircle, Clock, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTrust } from '../context/TrustContext';
import BottomNav from '../components/common/BottomNav';
import VendorOnboarding from '../components/vendor/VendorOnboarding';
import { updateTerminology, getMemberMessage } from '../utils/memberLanguage';

const VendorShowcasePage = () => {
  const navigate = useNavigate();
  const { user, isLoggedIn } = useAuth();
  const { userTrust } = useTrust();
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // Mock vendor success stories
  const vendorStories = [
    {
      id: 1,
      name: "Green Acres Farm",
      location: "Sacramento, CA",
      category: "Produce",
      story: "Sales increased by 40% in the first month after joining UniLedge. We're now able to plan our harvests better with predictable bulk orders.",
      avatar: "GA",
      stats: {
        monthlyRevenue: "$12,450",
        membersServed: 156,
        avgRating: 4.9
      }
    },
    {
      id: 2,
      name: "Sacramento Roasters",
      location: "Sacramento, CA",
      category: "Coffee",
      story: "UniLedge helped us reach new customers who appreciate quality coffee but found our retail prices too high. Bulk orders work perfectly!",
      avatar: "SR",
      stats: {
        monthlyRevenue: "$8,200",
        membersServed: 89,
        avgRating: 4.8
      }
    },
    {
      id: 3,
      name: "Artisan Soap Works",
      location: "Davis, CA",
      category: "Personal Care",
      story: "The platform jobs feature is amazing! We found reliable help for weekend markets through the community.",
      avatar: "AS",
      stats: {
        monthlyRevenue: "$5,600",
        membersServed: 45,
        avgRating: 5.0
      }
    }
  ];
  
  // Mock vendor benefits data
  const benefits = [
    {
      title: "Guaranteed Volume",
      description: "Sell in bulk with committed purchases from community groups",
      icon: ShoppingBag,
      color: "bg-blue-100 text-blue-600",
      stat: "85% order completion rate"
    },
    {
      title: `Direct ${updateTerminology('Member')} Access`,
      description: `Connect directly with local ${updateTerminology('members')} who value quality`,
      icon: Users,
      color: "bg-green-100 text-green-600",
      stat: "Zero middleman fees"
    },
    {
      title: "Predictable Demand",
      description: "Plan production with advance group orders and commitments",
      icon: TrendingUp,
      color: "bg-purple-100 text-purple-600",
      stat: "7-day advance notice"
    },
    {
      title: "Fair Pricing",
      description: "You keep 85% of every sale - transparent fee structure",
      icon: DollarSign,
      color: "bg-yellow-100 text-yellow-600",
      stat: "7.5% platform fee only"
    },
    {
      title: "Business Analytics",
      description: `Access insights about your ${updateTerminology('members')} and sales trends`,
      icon: BarChart4,
      color: "bg-indigo-100 text-indigo-600",
      stat: "Real-time dashboard"
    },
    {
      title: "Logistics Support",
      description: `Find skilled ${updateTerminology('members')} to help with fulfillment`,
      icon: Truck,
      color: "bg-orange-100 text-orange-600",
      stat: "Token-based payments"
    }
  ];

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleApplyNow = () => {
    if (!isLoggedIn) {
      navigate('/register', { state: { from: '/vendors', intent: 'vendor' } });
      return;
    }
    
    // Check if already a vendor
    const vendors = JSON.parse(localStorage.getItem('vendors') || '[]');
    const isVendor = vendors.some(v => v.createdBy === user?.id);
    
    if (isVendor) {
      navigate('/vendor/dashboard');
    } else {
      setShowOnboarding(true);
    }
  };

  const handleOnboardingSuccess = (vendorData) => {
    setShowOnboarding(false);
    navigate('/vendor/dashboard');
  };

  return (
    <div className="pb-20 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-800 p-6 relative">
        <button 
          className="absolute top-6 left-4 w-8 h-8 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
          onClick={handleGoBack}
        >
          <ArrowLeft size={20} />
        </button>
        <div className="text-center text-white mt-4">
          <Store size={40} className="mx-auto mb-3" />
          <h1 className="text-3xl font-bold">Grow Your Business with UniLedge</h1>
          <p className="mt-3 text-blue-100 max-w-2xl mx-auto">
            Join our community marketplace where local vendors thrive through bulk orders and direct {updateTerminology('member')} connections
          </p>
          <div className="mt-6 flex justify-center space-x-4 text-sm">
            <div className="flex items-center">
              <CheckCircle size={16} className="mr-1" />
              <span>No monthly fees</span>
            </div>
            <div className="flex items-center">
              <CheckCircle size={16} className="mr-1" />
              <span>Keep 85% of sales</span>
            </div>
            <div className="flex items-center">
              <CheckCircle size={16} className="mr-1" />
              <span>Instant setup</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Key Benefits */}
      <div className="px-4 py-8">
        <h2 className="text-2xl font-bold text-center mb-8">Why Vendors Love UniLedge</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0 ${benefit.color}`}>
                  <benefit.icon size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">{benefit.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">{benefit.description}</p>
                  <p className="text-xs font-medium text-gray-700 bg-gray-100 inline-block px-2 py-1 rounded">
                    {benefit.stat}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-white py-8">
        <div className="px-4 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Simple 3-Step Process</h2>
          <div className="space-y-6">
            <div className="flex items-center">
              <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mr-4 flex-shrink-0">
                1
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Quick 5-Minute Setup</h3>
                <p className="text-gray-600">Create your vendor profile, add products, and set bulk pricing tiers</p>
              </div>
              <Clock size={20} className="text-gray-400 ml-4" />
            </div>
            
            <div className="flex items-center">
              <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mr-4 flex-shrink-0">
                2
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Receive Group Orders</h3>
                <p className="text-gray-600">{updateTerminology('Members')} form groups to buy in bulk at discounted prices</p>
              </div>
              <ShoppingBag size={20} className="text-gray-400 ml-4" />
            </div>
            
            <div className="flex items-center">
              <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold mr-4 flex-shrink-0">
                3
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Fulfill & Get Paid</h3>
                <p className="text-gray-600">Deliver to central pickup points and receive payment weekly</p>
              </div>
              <DollarSign size={20} className="text-gray-400 ml-4" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Success Stories */}
      <div className="px-4 py-8">
        <h2 className="text-2xl font-bold text-center mb-8">Vendor Success Stories</h2>
        <div className="space-y-4 max-w-4xl mx-auto">
          {vendorStories.map((vendor) => (
            <div key={vendor.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4 flex-shrink-0">
                  {vendor.avatar}
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{vendor.name}</h3>
                      <div className="flex items-center text-sm text-gray-600 mb-1">
                        <span>{vendor.location}</span>
                        <span className="mx-2">•</span>
                        <span>{vendor.category}</span>
                      </div>
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} size={16} className="text-yellow-500" fill="currentColor" />
                        ))}
                        <span className="ml-2 text-sm font-medium">{vendor.stats.avgRating}</span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-4 italic">"{vendor.story}"</p>
                  
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{vendor.stats.monthlyRevenue}</p>
                      <p className="text-xs text-gray-600">Monthly Revenue</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{vendor.stats.membersServed}</p>
                      <p className="text-xs text-gray-600">{updateTerminology('Members')} Served</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">{vendor.stats.avgRating}</p>
                      <p className="text-xs text-gray-600">Average Rating</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Platform Features */}
      <div className="bg-gray-100 py-8">
        <div className="px-4 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Platform Features</h2>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3 flex items-center">
                  <BarChart4 className="mr-2 text-blue-600" size={20} />
                  Real-Time Analytics Dashboard
                </h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Track sales and revenue in real-time</li>
                  <li>• Monitor {updateTerminology('member')} engagement metrics</li>
                  <li>• Export data for accounting</li>
                  <li>• Identify top-performing products</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3 flex items-center">
                  <Users className="mr-2 text-green-600" size={20} />
                  Workforce Marketplace
                </h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Post logistics jobs to find helpers</li>
                  <li>• Access verified, skilled {updateTerminology('members')}</li>
                  <li>• Pay with tokens (no cash needed)</li>
                  <li>• Build reliable local workforce</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3 flex items-center">
                  <ShoppingBag className="mr-2 text-purple-600" size={20} />
                  Smart Order Management
                </h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Automated order confirmation</li>
                  <li>• Bulk printing of order lists</li>
                  <li>• {updateTerminology('Member')} communication tools</li>
                  <li>• Pickup coordination features</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold mb-3 flex items-center">
                  <DollarSign className="mr-2 text-yellow-600" size={20} />
                  Transparent Pricing
                </h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• 7.5% platform fee per transaction</li>
                  <li>• 7.5% goes to community logistics</li>
                  <li>• You keep 85% of every sale</li>
                  <li>• No hidden fees or charges</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Requirements */}
      <div className="px-4 py-6">
        <div className="max-w-2xl mx-auto bg-blue-50 rounded-lg p-6 border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
            <Store className="mr-2" size={20} />
            Vendor Requirements
          </h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start">
              <CheckCircle size={16} className="mr-2 mt-0.5 text-blue-600" />
              <span>Minimum trust score of 3.5 (current score: {userTrust?.score || 'N/A'})</span>
            </li>
            <li className="flex items-start">
              <CheckCircle size={16} className="mr-2 mt-0.5 text-blue-600" />
              <span>Valid business information and contact details</span>
            </li>
            <li className="flex items-start">
              <CheckCircle size={16} className="mr-2 mt-0.5 text-blue-600" />
              <span>Commitment to quality and timely fulfillment</span>
            </li>
            <li className="flex items-start">
              <CheckCircle size={16} className="mr-2 mt-0.5 text-blue-600" />
              <span>Acceptance of platform terms and fee structure</span>
            </li>
          </ul>
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="px-4 py-8">
        <div className="max-w-2xl mx-auto bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-3">Ready to Grow Your Business?</h3>
          <p className="mb-6 text-blue-100">
            Join our network of successful vendors and start reaching more {updateTerminology('members')} in your community.
          </p>
          <button 
            onClick={handleApplyNow}
            className="bg-white text-blue-700 px-8 py-3 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors shadow-md"
          >
            {isLoggedIn ? 'Start Vendor Application' : 'Sign Up to Become a Vendor'}
          </button>
          <p className="mt-4 text-sm text-blue-200">
            Setup takes less than 5 minutes • No credit card required
          </p>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="px-4 py-8 max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-2">How quickly can I start selling?</h3>
            <p className="text-gray-600">
              Once you complete the 5-minute setup, your products are immediately available to {updateTerminology('members')}. 
              Most vendors receive their first order within 48 hours.
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-2">When do I get paid?</h3>
            <p className="text-gray-600">
              Payments are processed weekly. You can choose between direct bank transfer or check payment.
              Orders are charged to {updateTerminology('members')} when groups close, and you receive payment after fulfillment.
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold mb-2">What support is available?</h3>
            <p className="text-gray-600">
              We provide a comprehensive vendor dashboard, help documentation, and dedicated vendor support.
              You can also post logistics jobs to find {updateTerminology('members')} who can help with order fulfillment.
            </p>
          </div>
        </div>
      </div>

      {/* Vendor Onboarding Modal */}
      <VendorOnboarding
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onSuccess={handleOnboardingSuccess}
      />
      
      <BottomNav />
    </div>
  );
};

export default VendorShowcasePage;